import type {AiChatAnimation} from '@Components/ai-chat-window/components/assistant-message-image-part-animation-creations/assistant-message-image-part-animation-creations.types';
import {ANIMATION_CREATION_SCROLL_NUDGE_DESKTOP_PX, ANIMATION_CREATION_SCROLL_NUDGE_PX, CHAT_MESSAGES_CONTAINER_ID} from '@Components/ai-chat-window/components/assistant-message-image-part-animation-creations/assistant-message-image-part-animation-creations.types';
import {isMobileScreenWidth} from '@Utils/dom.util';
import {AiChatCostType, ensureAiChatCreditsForCost} from '@Libraries/ai-chat.library';
import {pollableJob} from '@Libraries/pollable-job.library';
import {PollFailError, PollTimeoutError} from '@Libraries/pollable-job.library.types';
import {dispatchAiChatGeneratedAssetAdded} from '@AiChat/lib/ai-chat-assets-library';
import type {PollEnvelope, PollKickoffAck, PollTransportParams} from '@Libraries/pollable-job.library.types';

export enum AnimationGenerationErrorCode {
  INSUFFICIENT_CREDITS = 'insufficient_credits',
  BUSY = 'busy',
  SAFETY_CHILDREN = 'safety_children',
  SAFETY_SENSITIVE = 'safety_sensitive',
  SAFETY_COPYRIGHT = 'safety_copyright',
  ERROR = 'error',
  CANCELLED = 'cancelled',
}

export type AnimationGenerationError = Error & {code: AnimationGenerationErrorCode};

export function getAnimationErrorMessage(code: AnimationGenerationErrorCode | undefined): string {
  switch (code) {
    case AnimationGenerationErrorCode.BUSY:
      return window.i18next.t('pmwjs_animation_creation_error_busy');
    case AnimationGenerationErrorCode.SAFETY_CHILDREN:
      return window.i18next.t('pmwjs_animation_creation_error_children');
    case AnimationGenerationErrorCode.SAFETY_SENSITIVE:
      return window.i18next.t('pmwjs_animation_creation_error_sensitive');
    case AnimationGenerationErrorCode.SAFETY_COPYRIGHT:
      return window.i18next.t('pmwjs_animation_creation_error_copyright');
    default:
      return window.i18next.t('pmwjs_animation_creation_failed');
  }
}

export const ensureAnimationCredits = async (generateAudio: boolean): Promise<boolean> => {
  const cost = generateAudio ? AiChatCostType.ANIMATION_WITH_AUDIO_COST : AiChatCostType.ANIMATION_WITHOUT_AUDIO_COST;
  return ensureAiChatCreditsForCost(cost);
};

export async function cancelAnimationGeneration(pollingKey: string): Promise<void> {
  await window.PMW.writeLocal('ai/cancelAnimationGeneration', {key: pollingKey});
}

export interface GenerateAnimationOptions {
  signal?: AbortSignal;
  onPollingKeyReceived?: (pollingKey: string) => void;
  prompt?: string;
  duration?: number;
  generateAudio?: boolean;
}

const ANIMATION_GENERATION_POLL_TIMEOUT_MS = 300_000;
const ANIMATION_GENERATION_INSUFFICIENT_CREDITS_FAIL_CODE = 'INSUFFICIENT_CREDITS';
const DEFAULT_ANIMATION_DURATION_SECONDS = 6;

function toAnimationGenerationError(code: AnimationGenerationErrorCode, message: string): AnimationGenerationError {
  const err = new Error(message) as AnimationGenerationError;
  err.code = code;
  return err;
}

/**
 * Nudges the chat down so the freshly-revealed video-creation section is not clipped by the
 * viewport. Deferred a frame so the section has rendered before we scroll.
 */
function nudgeChatMessagesScroll(): void {
  let nudgePx = ANIMATION_CREATION_SCROLL_NUDGE_DESKTOP_PX;

  if (isMobileScreenWidth()) {
    nudgePx = ANIMATION_CREATION_SCROLL_NUDGE_PX;
  }

  requestAnimationFrame((): void => {
    document.getElementById(CHAT_MESSAGES_CONTAINER_ID)?.scrollBy({top: nudgePx, behavior: 'smooth'});
  });
}

export async function generateAnimationForAiChatImage(imageHashedId: string, options: GenerateAnimationOptions = {}): Promise<AiChatAnimation> {
  const {signal, onPollingKeyReceived, prompt, duration, generateAudio} = options;

  nudgeChatMessagesScroll();

  const initiateGeneration = async (): Promise<PollKickoffAck> => {
    return (await window.PMW.writeLocal('ai/generateAnimationForAiChatImageV2', {
      imageHashedId,
      prompt: prompt ?? '',
      duration: duration ?? DEFAULT_ANIMATION_DURATION_SECONDS,
      generateAudio: generateAudio ?? false,
    })) as PollKickoffAck;
  };

  const checkStatus = async (ack: PollKickoffAck, transportParams: PollTransportParams): Promise<PollEnvelope<AiChatAnimation>> => {
    if (signal?.aborted) {
      throw new DOMException('Animation generation cancelled by user', 'AbortError');
    }
    return (await window.PMW.readLocal('ai/checkAnimationGenerationStatusV2', {
      pollingKey: ack.pollingKey,
      ...transportParams,
    })) as PollEnvelope<AiChatAnimation>;
  };

  try {
    const animation = await pollableJob<AiChatAnimation>({
      kickoff: initiateGeneration,
      status: checkStatus,
      timeoutMs: ANIMATION_GENERATION_POLL_TIMEOUT_MS,
      onAck: (ack: PollKickoffAck): void => {
        onPollingKeyReceived?.(ack.pollingKey);
      },
    });
    if (signal?.aborted) {
      throw new DOMException('Animation generation cancelled by user', 'AbortError');
    }
    dispatchAiChatGeneratedAssetAdded();
    return animation;
  } catch (e) {
    if (e instanceof PollFailError) {
      const code = e.code === ANIMATION_GENERATION_INSUFFICIENT_CREDITS_FAIL_CODE ? AnimationGenerationErrorCode.INSUFFICIENT_CREDITS : (e.code as AnimationGenerationErrorCode);
      throw toAnimationGenerationError(code, 'Animation generation failed');
    }
    if (e instanceof PollTimeoutError) {
      throw toAnimationGenerationError(AnimationGenerationErrorCode.ERROR, 'Animation generation timed out');
    }
    throw e;
  }
}
